import React, { useState, useEffect } from 'react';
import { Brain } from 'lucide-react';
import Critter from '../glyphs/Critter';
import Glyph from '../glyphs/Glyph';
import { CRITTER_IDS } from '../glyphs/critters';

interface MemoryGameProps {
  onComplete: (score: number) => void;
}

// 32 drawn tokens for up to an 8x8 grid: the critter pack plus a few glyphs.
const CARDS_POOL = [
  ...CRITTER_IDS.map((id) => `c:${id}`),
  'g:sun', 'g:heart', 'g:sprout', 'g:star', 'g:rocket',
].slice(0, 32);

const CardFace = ({ token, size }: { token: string; size: number }) =>
  token.startsWith('c:') ? <Critter id={token.slice(2)} size={size} /> : <Glyph name={token.slice(2)} size={size * 0.8} />;

export default function MemoryGame({ onComplete }: MemoryGameProps) {
  const [board, setBoard] = useState<string[]>([]);
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matched, setMatched] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [gridSize, setGridSize] = useState<4 | 6 | 8 | null>(null);

  useEffect(() => {
    if (gridSize) {
      startGame(gridSize);
    }
  }, [gridSize]);

  const startGame = (size: 4 | 6 | 8) => {
    const numPairs = (size * size) / 2;
    const selectedCards = CARDS_POOL.slice(0, numPairs);
    const shuffled = [...selectedCards, ...selectedCards].sort(() => Math.random() - 0.5);
    setBoard(shuffled);
    setFlipped([]);
    setMatched([]);
    setScore(0);
  };

  useEffect(() => {
    if (flipped.length === 2) {
      const [first, second] = flipped;
      if (board[first] === board[second]) {
        setMatched([...matched, first, second]);
        setScore(s => s + 10);
        setFlipped([]);
      } else {
        setTimeout(() => setFlipped([]), 1000);
      }
    }
  }, [flipped, board, matched]);

  useEffect(() => {
    if (gridSize && board.length > 0 && matched.length === board.length) {
      setTimeout(() => onComplete(score + (gridSize * 10)), 1000);
    }
  }, [matched, board, onComplete, score, gridSize]);

  if (!gridSize) {
      return (
          <div className="flex flex-col items-center justify-center p-8 text-foreground h-full space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">בחר רמת קושי</h2>
              <div className="flex flex-col gap-4 w-full max-w-xs">
                  <button onClick={() => setGridSize(4)} className="p-4 bg-muted hover:bg-accent rounded-xl text-xl font-bold transition-all">
                      קל (4x4)
                  </button>
                  <button onClick={() => setGridSize(6)} className="p-4 bg-muted hover:bg-accent rounded-xl text-xl font-bold transition-all">
                      בינוני (6x6)
                  </button>
                  <button onClick={() => setGridSize(8)} className="p-4 bg-muted hover:bg-accent rounded-xl text-xl font-bold transition-all">
                      קשה (8x8)
                  </button>
              </div>
          </div>
      );
  }

  return (
    <div className="flex flex-col items-center h-full justify-center">
      <div className="flex items-center justify-between w-full max-w-md px-4 mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2 text-foreground">
            <Brain className="text-purple-400" /> משחק זיכרון
        </h2>
        <button onClick={() => setGridSize(null)} className="text-sm bg-muted px-3 py-1 rounded-lg text-muted-foreground">
            חזרה
        </button>
      </div>

      <div 
        className="grid gap-2 p-2 bg-muted/50 rounded-2xl"
        style={{ 
            gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` 
        }}
      >
        {board.map((card, i) => (
          <button
            key={i}
            onClick={() => !flipped.includes(i) && !matched.includes(i) && setFlipped([...flipped, i])}
            className={`
                aspect-square flex items-center justify-center rounded-lg transition-all duration-300 transform
                ${gridSize === 8 ? 'text-lg w-8 h-8 md:w-10 md:h-10' : gridSize === 6 ? 'text-2xl w-12 h-12' : 'text-3xl w-16 h-16'}
                ${flipped.includes(i) || matched.includes(i) ? 'bg-bridge-soft rotate-0' : 'bg-muted rotate-180 hover:bg-accent'}
            `}
          >
            {(flipped.includes(i) || matched.includes(i)) ? <CardFace token={card} size={gridSize === 8 ? 26 : gridSize === 6 ? 40 : 54} /> : ''}
          </button>
        ))}
      </div>
      
      <div className="mt-6 text-xl font-bold text-foreground">
        ניקוד: {score}
      </div>
    </div>
  );
}
